import { z } from "zod";
import { trimmed, uuid } from "./schemas/common";

/**
 * Households: the unit an invitation is addressed to (iOS `GuestHousehold`).
 * Derived, never stored as a relationship: `householdId` is a flat nullable
 * uuid, and a guest without one is a household of one, so every count works
 * whether or not anyone uses the feature.
 */
export interface HouseholdMember {
  id: string; householdId: string | null; householdName: string; firstName: string; lastName: string;
  email: string; phone: string; address: string; partyCount: number; plusOneAllowed: boolean;
}

export interface HouseholdGroup<G extends HouseholdMember> {
  /** `householdId`, or `solo-<guest id>` for a household of one. */
  key: string;
  householdId: string | null;
  /** First non-empty household name among the members ("" when none). */
  name: string;
  members: G[];
}

/** Groups guests into households, keeping the input order of each group's first member so lists stay stable. */
export function groupHouseholds<G extends HouseholdMember>(guests: readonly G[]): HouseholdGroup<G>[] {
  const order: string[] = [];
  const buckets = new Map<string, G[]>();
  const names = new Map<string, string>();
  for (const g of guests) {
    const key = g.householdId ?? `solo-${g.id}`;
    if (!buckets.has(key)) { order.push(key); buckets.set(key, []); }
    buckets.get(key)!.push(g);
    const candidate = g.householdName.trim();
    if (!names.get(key) && candidate) names.set(key, candidate);
  }
  return order.map((key) => ({ key, householdId: buckets.get(key)![0]!.householdId, name: names.get(key) ?? "", members: buckets.get(key)! }));
}

export type HouseholdLabel =
  | { kind: "name"; text: string }
  | { kind: "person"; text: string }
  | { kind: "surname"; surname: string }
  | { kind: "firstNames"; text: string };

/** iOS `displayName`: the household name; one member's full name; "The <surname> household"; otherwise the first names. */
export function householdLabel(g: HouseholdGroup<HouseholdMember>): HouseholdLabel {
  if (g.name) return { kind: "name", text: g.name };
  if (g.members.length === 1) {
    const m = g.members[0]!;
    return { kind: "person", text: `${m.firstName} ${m.lastName}`.trim() };
  }
  const surnames = new Set(g.members.map((m) => m.lastName.trim()).filter(Boolean));
  if (surnames.size === 1) return { kind: "surname", surname: [...surnames][0]! };
  return { kind: "firstNames", text: g.members.map((m) => m.firstName.trim()).filter(Boolean).join(", ") };
}

/** English rendering of `householdLabel` for text the server writes (link labels, activity). */
export function householdLabelEn(g: HouseholdGroup<HouseholdMember>): string {
  const l = householdLabel(g);
  return l.kind === "surname" ? `The ${l.surname} household` : l.text || "Unnamed Guest";
}

const firstNonEmpty = (xs: string[]) => xs.map((x) => x.trim()).find(Boolean) ?? null;
export const householdEmail = (g: HouseholdGroup<HouseholdMember>) => firstNonEmpty(g.members.map((m) => m.email));
export const householdPhone = (g: HouseholdGroup<HouseholdMember>) => firstNonEmpty(g.members.map((m) => m.phone));
export const householdAddress = (g: HouseholdGroup<HouseholdMember>) => firstNonEmpty(g.members.map((m) => m.address));
/** Heads the household brings, plus-ones included. */
export const householdHeads = (g: HouseholdGroup<HouseholdMember>) => g.members.reduce((s, m) => s + m.partyCount + (m.plusOneAllowed ? 1 : 0), 0);

/** The surname everyone shares, for a default household name ("" when they differ). */
export function sharedSurname(members: readonly Pick<HouseholdMember, "lastName">[]): string {
  const s = new Set(members.map((m) => m.lastName.trim()).filter(Boolean));
  return s.size === 1 ? [...s][0]! : "";
}

/* ───────── batch edits ───────── */

export const BATCH_MAX = 500;
export const batchIdsSchema = z.array(uuid).min(1).max(BATCH_MAX);
/** POST /weddings/:id/guests/households — put these guests into one household (keeps an existing id among them). */
export const groupHouseholdSchema = z.object({ ids: z.array(uuid).min(2).max(BATCH_MAX), name: trimmed(80).optional() });
export const ungroupHouseholdSchema = z.object({ ids: batchIdsSchema });
export type GroupHouseholdInput = z.infer<typeof groupHouseholdSchema>;
export type UngroupHouseholdInput = z.infer<typeof ungroupHouseholdSchema>;

/** Meta of batch edits: ids that are not live rows of this wedding are counted, never silently dropped. */
export interface BatchUpdateMeta { requested: number; updated: number; missing: number }
